import { h, Fragment } from "preact";
import { useState, useMemo, useContext, useEffect } from "preact/hooks";
import moment from "moment";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Pagination,
  Popover,
  Select,
  Table,
  Typography,
  Input,
  Radio,
  Cascader,
  Space,
  Checkbox,
} from "antd";
import {
  DownloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  RightOutlined, LeftOutlined
} from "@ant-design/icons";
import TransactionsDataSource from "./TransactionsDataSource";
import useTransactions from "./useTransactions";
import { getDefaultStateParams } from "./TransactionsUtils";
import TransactionsDownloadSection from "./TransactionsDownloadSection";
import TransactionModalPage from "./TransactionModalPage";
import FilterContentBy from "./components/FilterContentBy";
import { KeycloakContext } from "../../providers/KeycloakContext";
import ConsumerSelector from "./ConsumerIDSelector.jsx";

const { Option, OptGroup } = Select;

export default function TransactionsPage(props) {
  const { transactions, fetchTransactions, isLoading } = useTransactions();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState("");
  const [cacheState, setCacheState] = useState({});
  const [selectedConsumerId, setSelectedConsumerId] = useState(null);

  const handleFlowIdClick = (flowId) => {
    setSelectedFlowId(flowId);
    setModalVisible(true);
  };

  const handleConsumerSelect = (consumerId) => {
    setSelectedConsumerId(consumerId);
    localStorage.setItem("selectedConsumerId", consumerId);
    fetchTransactions({ params: filters });
  };

  const table =
    TransactionsDataSource(transactions).asTableView(handleFlowIdClick);
  const {
    queryParams,
    paginationParams,
    newQueryParams,
    filterOptions,
    createTransactionMap,
  } = getDefaultStateParams();

  const initialState = {
    dateFrom: newQueryParams.default_date_from.unix(),
    dateTo: newQueryParams.default_date_to.unix(),
    // serviceName: queryParams.defaultServiceName,
    recordsPerPage: paginationParams.defaultPageSize,
    pageNumber: paginationParams.initialPageIndex,
    selectedFilters: [],
    selectedSearchFilter: "",
    searchQuery: "",
    searchByFlowID: "",
    searchByPersonID: "",
    searchByIIN: "",
    currentFlowID: "",
    currentPersonID: "",
    currentIIN: "",
    selectedPeriod: "24",
    hasMoreData: true,
    customRange: false,
  };

  const [currentState, setCurrentState] = useState(initialState);

  const [filters, setFilters] = useState(initialState);
  const [userRoles, setUserRole] = useState([]);
  const [canManualEdit, setCanManualEdit] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(false);

  const canUserEditManually = () => {
    const roles = userRoles || [];
    return roles.includes("super-admin") || roles.includes("admin");
  };

  useEffect(() => {
    setCanManualEdit(canUserEditManually());
  }, [userRoles]);

  useEffect(() => {
    if (shouldSearch) {
      onSearchButtonClick(); 
      setShouldSearch(false);
    }
  }, [filters, shouldSearch]); // Depend on filters and shouldSearch

  useEffect(()=>{
    getConsumerIdFromLocalStorage();
  }, []);

  const disabledDateRange = (current) => current > newQueryParams.default_date_to;

  const onDateRangeChange = ([selectedDateFrom, selectedDateTo]) => {
    if (!selectedDateFrom || !selectedDateTo) return;
    const dateFrom = selectedDateFrom.startOf("day").unix();
    const dateTo = selectedDateTo.endOf("day").unix();
    setFilters({ ...filters, dateFrom, dateTo, pageNumber: 0, customRange: true });
    setShouldSearch(true);
  };

  const onSearchButtonClick = (event) => {
    setFilters((prevFilters) => ({ ...prevFilters, currentFlowID: filters.searchByFlowID, currentPersonID: filters.searchByPersonID, currentIIN: filters.searchByIIN, pageNumber: 0 }));
    fetchTransactions({ params: { ...filters, pageNumber: 0 } });
  };

  const getConsumerIdFromLocalStorage = () => {
    const currentConsumerID = localStorage.getItem("selectedConsumerId");
    setSelectedConsumerId(currentConsumerID);
  }

  const handleFilterInputChange = (variant, value) => {
    const { searchKey } = FilterContentBy.VARIANTS[variant];
    setFilters((prevFilters) => ({ ...prevFilters, [searchKey]: value }));
  };

  const onPageChange = (currentPageIndex) => {
    fetchTransactions({
      params: { ...filters, pageNumber: currentPageIndex },
    });
  };

  const updateCache = (flowId, data) => {
    setCacheState((prevCache) => ({
      ...prevCache,
      [flowId]: data,
    }));
  };
  
  const clearCache = () => {
    setCacheState({});
  }

  const handleFilterChange = (selectedValues, selectedOptions) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedFilters: [...selectedValues],
    }));
    setShouldSearch(true);
  };

  const refreshTransactions = () => {
    setFilters(prevFilters => ({
      ...prevFilters,
      dateTo: moment().unix(),
    }));
    clearCache();
    setShouldSearch(true);
  }

  const onPeriodChange = (e) => {
    let dateFrom, dateTo;
    if (e.target.value == "24") {
      dateFrom = moment().subtract(24, "hours");
    } else if (e.target.value == "7") {
      dateFrom = moment().subtract(7, "days");
    } else {
      dateFrom = moment().subtract(30, "days");
    }
    dateTo = moment();

    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        dateFrom: dateFrom.unix(),
        dateTo: dateTo.unix(),
        selectedPeriod: e.target.value,
        pageNumber: 0,
        customRange: false,
      };

      fetchTransactions({ params: updatedFilters });

      return updatedFilters;
    });
  };

  const handleResultsPerPageChange = (value) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        recordsPerPage: value,
        pageNumber: paginationParams.initialPageIndex,
      };

      fetchTransactions({ params: updatedFilters });

      return updatedFilters;
    });
  };

  //there was a different bug due to asynchronous nature of useState(), so ai suggested to use flag to trigger in useEffect
  const onFiltersClear = () => {
    setFilters({ ...initialState });
    setShouldSearch(true);
  };

  const clearFlowIDFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchByFlowID: "",
      currentFlowID: "",
    }));
    setShouldSearch(true);
  };

  const clearPersonIDFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchByPersonID: "",
      currentPersonID: "",
    }));
    setShouldSearch(true);
  };

  const clearIINFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchByIIN: "",
      currentIIN: "",
    }));
    setShouldSearch(true);
  }

  const convertDateFormats = () => {
    return [moment.unix(filters.dateFrom), moment.unix(filters.dateTo)];
  };

  const handleNextClick = () => {
    if (filters.hasMoreData) {
      setFilters((prevFilters) => {
        const updatedFilters = {
          ...prevFilters,
          pageNumber: prevFilters.pageNumber + 1,
        };

        fetchTransactions({ params: updatedFilters });

        return updatedFilters;
      });
    }
  };

  const handlePrevClick = () => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        pageNumber: prevFilters.pageNumber - 1,
      };

      fetchTransactions({ params: updatedFilters });

      return updatedFilters;
    });
  };

  const nextButtonDisabled = useMemo(() => {
    return parseInt(transactions?.length) !== parseInt(filters.recordsPerPage);
  }, [filters.recordsPerPage, transactions?.length]);

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const transactionsCardData = useMemo(() => {
    if (transactions?.length > 0) {
      const transactionMap = createTransactionMap(transactions);

      transactions.forEach(transaction => {
        if (transaction.session_projections) {
          const userIps = [...new Set(
            transaction.session_projections
              .map(session => session.user_ip)
              .filter(ip => ip)
          )];

          if (transactionMap[transaction.flow_id]) {
            transactionMap[transaction.flow_id].user_ips = userIps;
          }
        }
      });

      return transactionMap;
    }

    return {};
}, [transactions]);
  const hasFilters = (filters.currentFlowID || filters.currentPersonID || filters.currentIIN || filters.selectedFilters.length>0);
  return (
    <div style={{ minWidth: "900px" }}>
      <div className="main-box">
        <div className="upper">
          <div className="type-filters">
            <Cascader
              style={{
                minWidth: "250px",
              }}
              options={filterOptions}
              onChange={handleFilterChange}
              placeholder="Add filters"
              multiple
              id="filter"
              value={filters.selectedFilters}
              showCheckedStrategy={Cascader.SHOW_CHILD}
            />

            <p
              style={{
                textDecoration: "underline",
                marginBottom: "0px",
                cursor: "pointer",
              }}
              onClick={onFiltersClear}
            >
              {hasFilters ? "Clear filters" : null }
            </p>
          </div>
          <div className="date-filters">
            <div style={{display: "flex", gap: 5}}>
            <Button onClick={refreshTransactions}>
              <ReloadOutlined />
            </Button>
            <Radio.Group
              defaultValue="24"
              buttonStyle="solid"
              onChange={onPeriodChange}
              value={filters.customRange ? null : filters.selectedPeriod}
              id="period"
            >
              <Radio.Button value="24" id="24h" className="date-filter-option">
                24 hours
              </Radio.Button>
              <Radio.Button value="7" id="7d" className="date-filter-option">
                7 days
              </Radio.Button>
              <Radio.Button value="30" id="30d" className="date-filter-option">
                30 days
              </Radio.Button>
            </Radio.Group>
            </div>
            <DatePicker.RangePicker
              className="report__form-picker"
              defaultValue={[
                queryParams.defaultDateFrom,
                queryParams.defaultDateTo,
              ]}
              value={convertDateFormats()}
              disabledDate={disabledDateRange}
              onCalendarChange={onDateRangeChange}
            />
          </div>
        </div>
        <div className="lower">
          <div className="search-filters">
            <Popover
              placement="bottom"
              title="Filter by Flow ID"
              content={
                <FilterContentBy
                  variant="flowId"
                  value={filters.searchByFlowID}
                  onChange={(value) => handleFilterInputChange("flowId", value)}
                  onApply={onSearchButtonClick}
                />
              }
            >
              <Button>
                {!filters.currentFlowID ? (
                  <PlusCircleOutlined />
                ) : (
                  <MinusCircleOutlined onClick={clearFlowIDFilter} />
                )}{" "}
                Flow ID
                {filters.currentFlowID ? `: ${filters.currentFlowID}` : null}
              </Button>
            </Popover>
            <Popover
              placement="bottom"
              title="Filter by Person ID"
              content={
                <FilterContentBy
                  variant="personId"
                  value={filters.searchByPersonID}
                  onChange={(value) => handleFilterInputChange("personId", value)}
                  onApply={onSearchButtonClick}
                />
              }
            >
              <Button>
                {!filters.currentPersonID ? (
                  <PlusCircleOutlined />
                ) : (
                  <MinusCircleOutlined onClick={clearPersonIDFilter} />
                )}{" "}
                Person ID
                {filters.currentPersonID
                  ? `:  ${filters.currentPersonID}`
                  : null}
              </Button>
            </Popover>
            <Popover
              placement="bottom"
              title="Filter by IIN"
              content={
                <FilterContentBy
                  variant="iin"
                  value={filters.searchByIIN}
                  onChange={(value) => handleFilterInputChange("iin", value)}
                  onApply={onSearchButtonClick}
                  minLength={4}
                />
              }
            >
              <Button>
                {!filters.currentIIN ? (
                  <PlusCircleOutlined />
                ) : (
                  <MinusCircleOutlined onClick={clearIINFilter} />
                )}{" "}
                IIN
                {filters.currentIIN
                  ? `:  ${filters.currentIIN}`
                  : null}
              </Button>
            </Popover>
            <TransactionsDownloadSection propData={filters} />
          </div>

          <div className="results-per-page">
            <p
              style={{
                color: "#B6BFDA",
                fontWeight: "400",
                marginBottom: "0px",
                fontSize: "14px",
                lineHeight: "24px",
                letterSpacing: "-0.24px",
                fontStyle: "normal",
              }}
            >
              Results per page
            </p>
            <Select
              defaultValue="25"
              style={{ width: "70px" }}
              onChange={handleResultsPerPageChange}
              value={filters.recordsPerPage}
            >
              <Option value="10" id="10">
                10
              </Option>
              <Option value="25" id="25">
                25
              </Option>
              <Option value="50" id="50">
                50
              </Option>
            </Select>
            {/* <Typography.Text style={{ color: "gray" }}>
                Showed {table.rows.length} of {table.total} transactions
              </Typography.Text> */}
          </div>
        </div>
      </div>
      <ConsumerSelector selectedConsumerId={selectedConsumerId} onConsumerSelect={handleConsumerSelect} />
      <Card
        bordered={false}
        headStyle={{
          borderBottom: "none",
        }}
        className="transaction-page"
      >
        <Table
          columns={table.columns}
          dataSource={table.rows}
          loading={isLoading}
          pagination={false}
        />
        <TransactionModalPage
          visible={modalVisible}
          onCancel={handleCloseModal}
          flowId={selectedFlowId}
          transactionInfo={transactionsCardData[selectedFlowId]}
          canManualEdit={canManualEdit}
          cacheState={cacheState}
          consumerId={selectedConsumerId}
          updateCache={updateCache}
        />
        {/* // pagination={{
          //   onChange: onPageChange,
          //   showSizeChanger: false,
          //   showLessItems: false,
          //   showQuickJumper: false,
          //   showPrevNextJumpers: true,
          //   // pageSize: filters.recordsPerPage,
          //   // total: table.total,
          // }} */}
        {/* expandable={{
            expandedRowRender: (record) => (
              <p style={{ margin: 0 }}>{record.description}</p>
            ),
            rowExpandable: (record) => !!record.description,
          }} */}
        <div className="pagination-buttons">
          <Button icon={<RightOutlined />} onClick={handleNextClick} disabled={nextButtonDisabled}>
            Next
          </Button>
          <Button icon={<LeftOutlined />} onClick={handlePrevClick} disabled={filters.pageNumber === 0}>
            Previous
          </Button>
        </div>
      </Card>
    </div>
  );
}
